import { useCallback, useEffect, useState } from "react";
import { Badge, Flex, Table } from "@mantine/core";
import styled from "styled-components";
import ScrollBox from "../components/common/Table/ScrollBox";
import { useLocation, useNavigate } from "react-router";
import Container from "../components/common/Container";
import PageHeader from "../components/common/PageHeader";
import CustomSelect from "../components/common/Inputs/CustomSelect";
import CustomPagination from "../components/common/CustomPagination";
import PlusIcon from "../components/common/Icons/PlusIcon";
import TableInfoButton from "../components/common/Buttons/TableInfoButton";
import { getDepartmentSelectData } from "../api/departmentApi";
import { ShiftRotation, getShiftRotations } from "../api/shiftRotationApi";
import { formatDayKey } from "../utils/shiftPattern";

/**
 * ROTA LİSTESİ (plan 2.34).
 *
 * Varsayılan olarak yalnızca YÜRÜRLÜKTEKİ rotalar geliyor (backend süzüyor);
 * "Geçmiş rotaları da göster" ile arşiv açılıyor. Geçmiş rotalar silinmiyor,
 * kapatılıyor — Eylül'ün puantajı Ekim'de rota değişse de değişmemeli.
 */
const PAGE_SIZE = 10;

const ShiftRotations = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [rotations, setRotations] = useState<ShiftRotation[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const [showExpired, setShowExpired] = useState<string | null>("no");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data: any = await getShiftRotations({
        pageNumber: page,
        limit: PAGE_SIZE,
        departmentId: selectedDepartment ?? undefined,
        includeExpired: showExpired === "yes" ? "1" : undefined,
      });
      setRotations(data?.data ?? []);
      setTotalPages(data?.meta?.totalPages ?? 1);
    } catch {
      setRotations([]);
    } finally {
      setLoading(false);
    }
  }, [page, selectedDepartment, showExpired]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getDepartmentSelectData()
      .then(({ data }: any) => setDepartmentOptions(data?.data ?? []))
      .catch(() => setDepartmentOptions([]));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [selectedDepartment, showExpired]);

  return (
    <Container>
      <PageHeader
        pageTitle="Rotalar"
        subText="Departmana bağlı dönüşümlü vardiya çevrimi. Kimin hangi gün hangi vardiyada olduğu buradan hesaplanır, elle girilmez."
        isButtoned
        buttonIcon={<PlusIcon />}
        buttonText="Yeni Rota"
        buttonOnClick={() => navigate(`${location.pathname}/new`)}
      />

      <Flex mb={20} gap={16} align="flex-end" wrap="wrap">
        <CustomSelect
          label="Departman"
          placeholder="Tümü"
          data={departmentOptions}
          value={selectedDepartment}
          onChange={setSelectedDepartment}
          clearable
          searchable
          w={240}
        />
        <CustomSelect
          label="Kapanmış rotalar"
          data={[
            { value: "no", label: "Gizle" },
            { value: "yes", label: "Göster" },
          ]}
          value={showExpired}
          onChange={setShowExpired}
          w={180}
        />
      </Flex>

      {!loading && rotations.length === 0 ? (
        <EmptyState>
          Henüz rota kurulmamış. Önce <strong>Ekipler</strong> ekranından
          departmanın ekiplerini açın, sonra "Yeni Rota" ile çevrimi kurun (ör.{" "}
          <strong>A → Sabah, B → Öğle, C → Akşam</strong>, haftalık dönen).
        </EmptyState>
      ) : (
        <TableWrapper>
          <Table style={{ borderCollapse: "separate" }} verticalSpacing="sm">
            <Table.Thead style={{ background: "#EEF2FF" }}>
              <Table.Tr>
                <StyledTh style={{ width: "26%" }}>Rota</StyledTh>
                <StyledTh style={{ width: "18%" }}>Departman</StyledTh>
                <StyledTh style={{ width: "14%" }}>Çevrim</StyledTh>
                <StyledTh style={{ width: "14%" }}>Periyot</StyledTh>
                <StyledTh style={{ width: "20%" }}>Geçerlilik</StyledTh>
                <StyledTh style={{ width: "8%" }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rotations.map((rotation) => {
                const restCount = rotation.shiftDefinitionIds.filter(
                  (id) => id == null
                ).length;
                return (
                  <StyledTr
                    key={rotation._id}
                    onClick={() => navigate(`${location.pathname}/${rotation._id}`)}
                  >
                    <Table.Td>{rotation.name}</Table.Td>
                    <Table.Td style={{ color: "#4b5563" }}>
                      {rotation.department?.name ?? "—"}
                    </Table.Td>
                    <Table.Td style={{ color: "#4b5563" }}>
                      {rotation.teamIds.length} ekip ·{" "}
                      {rotation.shiftDefinitionIds.length - restCount} vardiya
                      {restCount > 0 && (
                        <RestNote> + {restCount} dinlenme</RestNote>
                      )}
                    </Table.Td>
                    <Table.Td style={{ color: "#4b5563" }}>
                      {rotation.periodCount === 1
                        ? rotation.periodType === "month"
                          ? "Aylık"
                          : "Haftalık"
                        : `${rotation.periodCount} ${
                            rotation.periodType === "month" ? "ayda" : "haftada"
                          } bir`}
                    </Table.Td>
                    <Table.Td style={{ color: "#4b5563" }}>
                      {formatDayKey(rotation.startDate)} –{" "}
                      {rotation.endDate ? (
                        formatDayKey(rotation.endDate)
                      ) : (
                        <Badge variant="light" color="teal">
                          süresiz
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Flex justify="flex-end">
                        <TableInfoButton
                          onClick={(e: any) => {
                            e.stopPropagation();
                            navigate(`${location.pathname}/${rotation._id}`);
                          }}
                        />
                      </Flex>
                    </Table.Td>
                  </StyledTr>
                );
              })}
            </Table.Tbody>
          </Table>
        </TableWrapper>
      )}

      {totalPages > 1 && (
        <PaginationWrapper>
          <CustomPagination total={totalPages} value={page} onChange={setPage} />
        </PaginationWrapper>
      )}
    </Container>
  );
};

export default ShiftRotations;

const TableWrapper = styled(ScrollBox)`
  overflow-x: auto;
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const EmptyState = styled.div`
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  padding: 32px;
  color: #6b7280;
  font-size: 15px;
  line-height: 22px;
`;

const RestNote = styled.span`
  color: #9ca3af;
`;

const StyledTh = styled(Table.Td)`
  color: #4c4c4c;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
`;

const StyledTr = styled(Table.Tr)`
  cursor: pointer;
  color: #111827;
  font-size: 16px;
  font-weight: 400;
  &:hover {
    background: #f1f3f5 !important;
  }
`;
